import React, { useEffect, useState } from "react";
import {
  Plus, Trash2, Edit, RefreshCw, Filter, Users, X,
} from "lucide-react";
import { toast } from "react-toastify";
import {
  listContactLists,
  createContactList,
  updateContactList,
  deleteContactList,
  refreshContactList,
} from "../../apis/contactLists";
import SegmentBuilder from "../../components/contacts/segmentBuilder/SegmentBuilder";

const ContactListsPage = ({ phoneId }) => {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [builderOpen, setBuilderOpen] = useState(false);

  const fetchLists = async () => {
    if (!phoneId) return;
    setLoading(true);
    try {
      const r = await listContactLists({ phone_id: phoneId, limit: 200 });
      setLists(r.data.lists || []);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to load lists");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLists(); }, [phoneId]);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this list? Members are unlinked but contacts remain.")) return;
    try {
      await deleteContactList(id, phoneId);
      toast.success("List deleted");
      fetchLists();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to delete");
    }
  };

  const onRefresh = async (id) => {
    try {
      const r = await refreshContactList(id, phoneId);
      toast.success(`Refreshed: +${r.data.added || 0} member(s)`);
      fetchLists();
    } catch (err) {
      toast.error(err.response?.data?.error || "Refresh failed");
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Contact Lists</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Group contacts statically (CSV / manual) or dynamically (rules).
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setBuilderOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-200 rounded-md hover:bg-gray-50"
            >
              <Filter size={14} /> Build segment
            </button>
            <button
              onClick={() => setEditing({})}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700"
            >
              <Plus size={14} /> New list
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="px-3 py-2.5 text-left">Name</th>
                <th className="px-3 py-2.5 text-left">Type</th>
                <th className="px-3 py-2.5 text-left">Members</th>
                <th className="px-3 py-2.5 text-left">Status</th>
                <th className="px-3 py-2.5 text-left">Created</th>
                <th className="px-3 py-2.5 w-32" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-400">Loading…</td></tr>
              ) : lists.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-12 text-center text-gray-400">No lists yet.</td></tr>
              ) : lists.map((l) => (
                <tr key={l.id} className="border-t border-gray-100">
                  <td className="px-3 py-2.5 font-medium text-gray-900">{l.name}</td>
                  <td className="px-3 py-2.5">
                    <span className={`px-2 py-0.5 text-xs rounded-full capitalize ${l.type === "dynamic" ? "bg-violet-50 text-violet-700" : "bg-indigo-50 text-indigo-700"}`}>
                      {l.type}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-gray-700">
                    <span className="inline-flex items-center gap-1">
                      <Users size={13} className="text-gray-400" />
                      {(l.member_count || 0).toLocaleString()}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`px-2 py-0.5 text-xs rounded-full ${l.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-700"}`}>{l.status}</span>
                  </td>
                  <td className="px-3 py-2.5 text-gray-600">
                    {l.created_at ? new Date(l.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    {l.type === "dynamic" && (
                      <button onClick={() => onRefresh(l.id)} className="p-1.5 rounded text-gray-600 hover:bg-gray-100" title="Refresh">
                        <RefreshCw size={14} />
                      </button>
                    )}
                    <button onClick={() => setEditing(l)} className="p-1.5 rounded text-gray-600 hover:bg-gray-100" title="Edit">
                      <Edit size={14} />
                    </button>
                    <button onClick={() => onDelete(l.id)} className="p-1.5 rounded text-rose-600 hover:bg-rose-50" title="Delete">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <ContactListFormModal
          phoneId={phoneId}
          list={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); fetchLists(); }}
        />
      )}

      {builderOpen && (
        <SegmentBuilder
          phoneId={phoneId}
          onClose={() => setBuilderOpen(false)}
          onSaved={() => { setBuilderOpen(false); fetchLists(); }}
        />
      )}
    </div>
  );
};

const ContactListFormModal = ({ phoneId, list, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: list?.name || "",
    description: list?.description || "",
    type: list?.type || "static",
    status: list?.status || "active",
  });
  const [saving, setSaving] = useState(false);
  const isEdit = !!list?.id;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await updateContactList(list.id, { phone_id: phoneId, ...form });
        toast.success("List updated");
      } else {
        await createContactList({ phone_id: phoneId, ...form });
        toast.success("List created");
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />
      <div className="fixed top-0 right-0 bottom-0 w-full max-w-md bg-white shadow-2xl z-50 flex flex-col">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-base font-semibold">{isEdit ? "Edit list" : "New list"}</h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-gray-100"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          <FormField label="Name">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="lf-input" />
          </FormField>
          <FormField label="Description">
            <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="lf-input" />
          </FormField>
          <FormField label="Status">
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="lf-input">
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="archived">Archived</option>
            </select>
          </FormField>
          <style>{`.lf-input { display:block; width:100%; font-size:0.875rem; border:1px solid #e5e7eb; border-radius:0.375rem; padding:0.5rem 0.75rem; }`}</style>
        </form>
        <div className="px-5 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-2">
          <button onClick={onClose} type="button" className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md">Cancel</button>
          <button onClick={handleSubmit} disabled={saving} className="px-3 py-2 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-60">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </>
  );
};

const FormField = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-medium text-gray-600">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

export default ContactListsPage;
